import React, {useRef, useEffect} from 'react'
import {RouteTemplate} from 'server/functions/route/route.types'
import {Colors} from '@components/UI/index.js'
import ChooseRoutePoint from '@components/routes/ChooseRoutePoint.js'
import {formSpacing, ColorPicker, FormRow, FormStack, Input, DatePicker, TimePicker, Select, Recurrence} from '@components/UI/forms/index.js'
import useVehicleStore from 'src/stores/VehicleStore.js'
import t from 'src/helpers/Languages.js'
import {Reseller, User} from 'types/types.js'
import time from 'server/utils/time'
import {getAvailableBranches} from 'src/utils/branches.js'
import type {RecurrenceRef} from '@components/UI/forms/Recurrence.js'

 type MainFormFieldsProps = {
     template: RouteTemplate
     onChange: (template: RouteTemplate) => void
     messengers: User[]
     reseller: Reseller
     nameInvalid: boolean
     setNameInvalid: (invalid: boolean) => void
 }

function MainFormFields({template, onChange, messengers, reseller, nameInvalid, setNameInvalid}: MainFormFieldsProps) {
    const rruleRef = useRef<RecurrenceRef>(null)
    const vehicles = useVehicleStore((state) => state.vehicles)

    useEffect(() => {
        if (rruleRef.current) {
            if (!template.rrule) {
                const newRrule = rruleRef.current.init()
                onChange({...template, rrule: newRrule})
            }
        }
    }, [])

    return (
        <FormStack style={{marginBottom: formSpacing.rowGap}}>
            <FormRow>
                <FormRow gap={formSpacing.tight} style={{flex: 1}}>
                    <ColorPicker
                        value={template.color}
                        colors={Colors.routeColors}
                        onChange={(event) => onChange({...template, color: event.target.value})}
                    />

                    <Input
                        label={t('name')}
                        value={template.name}
                        onChange={(event) => {
                            onChange({...template, name: event.target.value})

                            if (event.target.value && nameInvalid) {
                                setNameInvalid(false)
                            }
                        }}
                        isInvalid={nameInvalid}
                    />
                </FormRow>

                {getAvailableBranches(reseller).length > 0 &&
                    <Select
                        label={t('branch')}
                        value={template.branchId || ''}
                        options={getAvailableBranches(reseller).map((branch) => {
                            return {value: branch.id, title: branch.name}
                        })}
                        onChange={(event) => {
                            if ('target' in event) {
                                onChange({...template, branchId: event.target.value})
                            }
                        }}
                        allowEmpty
                    />
                }
            </FormRow>

            <FormRow>
                <DatePicker
                    label={t('startDate')}
                    value={template.startDate}
                    minDate={time.today()}
                    onChange={(event) => {
                        if ('target' in event && event.target.value) {
                            onChange({...template, startDate: event.target.value})
                        }
                    }}
                    disabled={!!template._id}
                />

                <DatePicker
                    label={t('endDate')}
                    value={template.endDate}
                    minDate={(template.startDate && template.startDate > time.today()) ? template.startDate : time.today()}
                    onChange={(event) => {
                        if ('target' in event) {
                            onChange({...template, endDate: event.target.value})
                        }
                    }}
                    allowEmpty
                />
            </FormRow>

            <Recurrence
                date={template.startDate}
                rrule={template.rrule}
                onChange={(event) => {
                    onChange({...template, rrule: event.target.value})
                }}
                ref={rruleRef}
                direction='row'
            />

            <FormRow>
                <Select
                    label={t('messenger')}
                    value={template.messengerId}
                    options={messengers.map((m) => ({title: m.name, value: m._id}))}
                    onChange={(event) => onChange({...template, messengerId: event.target.value})}
                    allowEmpty
                />

                <TimePicker
                    label={t('startTime')}
                    value={template.startTime}
                    onChange={(event) => onChange({...template, startTime: event.target.value})}
                    allowEmpty
                />
            </FormRow>

            <FormRow>
                <Select
                    label={t('vehicle')}
                    value={template.vehicleTypeId || reseller.settings.defaultVehicle}
                    subvalue={template.vehicleId}
                    groupSubvalues
                    options={[
                        ...reseller.settings.vehicleTypes.filter((vT) => !vT.isArchived).map((vehicleType) => ({
                            value: vehicleType._id,
                            title: vehicleType.name,
                            icon: `mdi mdi-${vehicleType.icon}`
                        })),
                        ...vehicles.filter((v) => !v.isArchived).map((vehicle) => {
                            const activeVehicleType = reseller.settings.vehicleTypes.find((type) => type._id === vehicle.typeId)
                            return {
                                value: activeVehicleType?._id || vehicle._id,
                                ...(activeVehicleType && {subvalue: vehicle._id}),
                                title: vehicle.name,
                                ...(activeVehicleType && {icon: `mdi mdi-${activeVehicleType.icon}`})
                            }
                        })
                    ]}
                    onChange={(event) => {
                        const vehicleType = reseller.settings.vehicleTypes.find((vT) => vT._id === event.target.value)

                        if (!vehicleType) {
                            return
                        }

                        template.vehicleId = event.target.subvalue || ''
                        template.vehicleType = vehicleType.routeProfile
                        template.vehicleTypeId = vehicleType._id

                        if (reseller.permissions.vehicles && vehicleType) {
                            template.stopTime = vehicleType.stopTime
                        }

                        onChange(template)
                    }}
                />

                <Input
                    type='number'
                    label={t('stopTimePerAddress')}
                    value={template.stopTime}
                    onChange={(event) => onChange({...template, stopTime: parseInt(event.target.value)})}
                    append='s'
                />
            </FormRow>

            <ChooseRoutePoint
                label={t('startLocation')}
                address={template.startPoint}
                reseller={reseller}
                onChange={(address) => onChange({...template, startPoint: address})}
                route={template}
            />

            <ChooseRoutePoint
                label={t('endLocation')}
                address={template.endPoint}
                reseller={reseller}
                onChange={(address) => onChange({...template, endPoint: address})}
                route={template}
            />

            <Input
                label={t('instructions')}
                value={template.instructions}
                onChange={(event) => onChange({...template, instructions: event.target.value})}
            />
        </FormStack>
    )
}

export default MainFormFields
